import React, { useContext } from 'react';
import { settings, version, urls } from './config';
import { isNil } from 'lodash';
import { isTokenExpired } from './helpers/utils';

export const ApiContext = React.createContext();

export const useApi = () => useContext(ApiContext);

export const Method = {
    GET: "GET",
    POST: "POST",
    PUT: "PUT",
    DELETE: "DELETE",
};

export const ApiProvider = ({ children }) => {

    const alertError = (error, responseCode, url) => {
        try {
            fetch(`${urls.eventcore}SignUpUI/alert-error`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "CC-Client": `Crowdcatcher v${version}`
                },
                body: JSON.stringify({
                    Error: error,
                    ResponseCode: responseCode,
                    Url: url
                })
            }).catch(() => {}); // Silently fail if error logging fails
        } catch (e) {
            // Don't block on error logging
        }
    };

    const basicQuery = (url, token, setResult, errorCode) =>{

        basicMutation(url, null, Method.GET, token, setResult, errorCode, null);
    }

    const basicMutation = (url, body, method, token, setResult, errorCode, resetForm) =>{

        let responseCode = 200;    

        let headers = {
            "Content-Type": "application/json",
            "CC-Client": `Crowdcatcher v${version}`
        };

        if (token){
            headers.Authorization = "Bearer " + token;
        }

        if (settings.isDebug && settings.defaultDebuggingEnabled){
            console.log(`${url} ${method}`);
            console.log(token);
            console.log(body);
        }
      
        fetch(url, {
            method: method,
            headers: headers,
            body: body ? JSON.stringify(body) : null,
        })
        .then(response=>{
            responseCode = response.status;
            if (settings.isDebug && settings.defaultDebuggingEnabled){
                console.log(`${url} ${responseCode}`);
            }
            if (responseCode === 404){
                setResult(null, responseCode, resetForm);
                return null;
            }
            else if (responseCode === 401){
                
                // if the user is authenticated, redirect to login page (suggests an expired token or insufficient permissions)
                if (!isNil(token) && isTokenExpired(token))  {
                    localStorage.removeItem('login');
                    document.cookie = 'cc_userdata=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
                    window.location = '/auth/login';
                    return null;
                }

                setResult(null, responseCode, resetForm);
                return null;
            } 
            else if (responseCode === 204){
                if (resetForm){
                    setResult(null, responseCode, resetForm);
                }
                else{
                    setResult(null, responseCode);
                }
                return null;
            } 
            else{
                return response.json();
            }
        })            
        .then(data=> {

            if (!data && responseCode !== 204){
                setResult(null, responseCode);
                return;
            }

            if (settings.isDebug && settings.defaultDebuggingEnabled){
                console.log(data);
            }
            
            if (responseCode >= 200 && responseCode <= 299 && responseCode !== 204){
                if (resetForm){
                    if (setResult){
                        setResult(data, responseCode, resetForm);
                    }
                }
                else{
                    if (setResult){
                        setResult(data, responseCode);
                    }
                }
            }
            else {
                if (resetForm){
                    if (setResult && responseCode !== 204){
                        setResult(data, responseCode, resetForm);
                    }
                }
                else{
                    if (data){
                        if (setResult){
                            setResult(data, responseCode);
                        }
                    }
                }
            }
        },
        (error)=>{
            if (settings.isDebug){
                console.log(`${responseCode} ${errorCode}`);
                console.log(url);
                console.log(token);
                if (error){
                    console.log(error);
                }
            }
            console.log(`FAILED!${errorCode ? "\n[error code: " + errorCode + "]" : ""}`);
        });
    }

    const basicQuerySynchronous = async (url, token, errorCode) => {
        return await basicMutationSynchronous(url, null, Method.GET, token, errorCode);
    }

    const basicMutationSynchronous = async (url, body, method, token, errorCode) =>{

        const headers = {
            "Content-Type": "application/json",
            "CC-Client": `Crowdcatcher v${version}`
        };

        if (token){
            headers.Authorization = "Bearer " + token;
        }

        if (settings.isDebug && settings.defaultDebuggingEnabled){
            console.log(`${url} ${method}`);
            console.log(token);
            console.log(body);
        }

        let response = await fetch(url, {
                method: method,
                headers: headers,
                body: body ? JSON.stringify(body) : null,
            });


        if (isNil(response)){
            return { Data: null, ResponseCode: 599 };
        }

        if (response.status < 200 || response.status >= 400 || response.status === 204){
            return { Data: null, ResponseCode: response.status };
        }

        const data = await response.json();
        const code = response.status;

        return { Data: data, ResponseCode: code };        
    }

    return (
        <ApiContext.Provider
            value={{
                basicQuery,
                basicMutation,
                basicQuerySynchronous,
                basicMutationSynchronous,
                alertError
            }}
        >
            {children}
        </ApiContext.Provider>
    );
};
